// src/components/tasks/TaskDetail/TaskDetailMetadata.tsx
// 任務詳情元數據區：統一渲染桌面和行動版

import React from 'react';
import { Task, TaskPriority, TaskEnergyLevel } from '../../../types/task';
import { CalendarCheck2, CalendarClock, Clock, Flag, Tag, AlertCircle, Zap, MapPin } from '../../common/icons';
import MetadataRow from '../MetadataRow';
import { formatDurationFromMinutes, formatCategoryName } from '../../../utils/formatters';
import { formatPlanForDisplay, getDateUrgency } from '../../../utils/dateRangeUtils';
import { PRIORITY_MAP, ENERGY_MAP } from '../../../config/taskStyles';

interface TaskDetailMetadataProps {
  task: Task;
  objectiveTitle?: string | null;
  onPlanClick: () => void;
  onBeforeClick: () => void;
  onDurationClick: () => void;
  onCategoryClick: () => void;
  onObjectiveClick: () => void;
  onPrioritySave: (priority: TaskPriority | null) => void;  // Debounced, sync
  onEnergySave: (energy: TaskEnergyLevel | null) => void;   // Debounced, sync
  onLocationSave: (location: string | null) => Promise<void>;
  variant: 'desktop' | 'mobile';
}

export const TaskDetailMetadata: React.FC<TaskDetailMetadataProps> = ({
  task,
  objectiveTitle,
  onPlanClick,
  onBeforeClick,
  onDurationClick,
  onCategoryClick,
  onObjectiveClick,
  onPrioritySave,
  onEnergySave,
  onLocationSave,
  variant
}) => {
  const isMobile = variant === 'mobile';
  const showLabelOnMobile = isMobile;

  // Plan 專用顏色計算（預設藍色，緊迫時變色）
  const getPlanAccentColor = (dateString: string | undefined): 'red-muted' | 'orange' | 'blue' => {
    if (!dateString) return 'blue';

    // 已關閉的任務維持藍色
    if (task.status === 'CLOSED') return 'blue';

    const urgency = getDateUrgency(dateString);
    switch (urgency) {
      case 'overdue': return 'red-muted';  // 低調紅色
      case 'urgent': return 'orange';       // 橙色
      default: return 'blue';               // 藍色（Plan 預設）
    }
  };

  // Before 專用顏色計算（預設灰色，緊迫時變色）
  const getBeforeAccentColor = (dateString: string | undefined): 'red-muted' | 'orange' | 'default' => {
    if (!dateString) return 'default';

    // 已關閉的任務維持灰色
    if (task.status === 'CLOSED') return 'default';

    const urgency = getDateUrgency(dateString);
    switch (urgency) {
      case 'overdue': return 'red-muted';  // 低調紅色
      case 'urgent': return 'orange';       // 橙色
      default: return 'default';            // 預設灰色
    }
  };

  const containerClass = isMobile
    ? 'md:hidden mt-6 px-4'
    : 'hidden md:flex md:w-52 md:shrink-0 md:flex-col';

  const innerClass = isMobile
    ? 'space-y-0'
    : 'metadata-sidebar sticky top-4 flex-1';

  return (
    <div className={containerClass}>
      {/* Mobile 分隔線 */}
      {isMobile && <div className="meta-divider mb-4" />}

      <div className={innerClass}>
        {/* Time-related Section */}
        <MetadataRow
          icon={CalendarCheck2}
          label="Plan"
          value={task.plan ? formatPlanForDisplay(task.plan) : undefined}
          accentColor={getPlanAccentColor(task.plan)}
          onClick={onPlanClick}
          onEdit={onPlanClick}
          showLabelOnMobile={showLabelOnMobile}
        />
        <MetadataRow
          icon={Clock}
          label="Duration"
          value={task.estimatedTime ? formatDurationFromMinutes(task.estimatedTime) : undefined}
          onClick={onDurationClick}
          showLabelOnMobile={showLabelOnMobile}
        />
        <MetadataRow
          icon={CalendarClock}
          label="Before"
          value={task.targetDate ? formatPlanForDisplay(task.targetDate) : undefined}
          accentColor={getBeforeAccentColor(task.targetDate)}
          onClick={onBeforeClick}
          showLabelOnMobile={showLabelOnMobile}
        />

        <div className="meta-divider" />

        {/* Properties Section */}
        <MetadataRow
          icon={Flag}
          label="Goal"
          value={objectiveTitle ?? undefined}
          onClick={onObjectiveClick}
          showLabelOnMobile={showLabelOnMobile}
        />
        <MetadataRow
          icon={Tag}
          label="Category"
          value={task.category ? formatCategoryName(task.category) : undefined}
          onClick={onCategoryClick}
          showLabelOnMobile={showLabelOnMobile}
        />
        <MetadataRow
          icon={AlertCircle}
          label="Priority"
          value={task.priority ? PRIORITY_MAP[task.priority as TaskPriority] : undefined}
          inlineCycle={{
            currentValue: task.priority,
            options: [
              { value: '1', label: 'High', color: 'red' },
              { value: '2', label: 'Medium', color: 'yellow' },
              { value: '3', label: 'Low', color: 'blue' },
            ],
            onSelect: (value) => onPrioritySave((value ?? null) as TaskPriority | null),
          }}
          showLabelOnMobile={showLabelOnMobile}
        />
        <MetadataRow
          icon={Zap}
          label="Energy"
          value={task.energyLevel ? ENERGY_MAP[task.energyLevel as TaskEnergyLevel] : undefined}
          inlineCycle={{
            currentValue: task.energyLevel,
            options: [
              { value: '1', label: 'High', color: 'red' },
              { value: '2', label: 'Medium', color: 'yellow' },
              { value: '3', label: 'Low', color: 'green' },
            ],
            onSelect: (value) => onEnergySave((value ?? null) as TaskEnergyLevel | null),
          }}
          showLabelOnMobile={showLabelOnMobile}
        />
        <MetadataRow
          icon={MapPin}
          label="Location"
          value={task.location}
          inlineEdit={{
            onSave: onLocationSave,
            placeholder: 'Location',
            maxLength: 100
          }}
          showLabelOnMobile={showLabelOnMobile}
        />

        <div className="meta-divider" />

        {/* Supplementary Info */}
        <div className="meta-row supplementary">
          <span className="meta-label">Created</span>
          <span className="meta-value">
            {task.createdAt
              ? new Date(task.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })
              : 'N/A'}
          </span>
        </div>

        {/* Origin Capture Link */}
        {task.originCaptureId && (
          <div className="meta-row supplementary">
            <span className="meta-label">Origin</span>
            <a
              href={`/captures?captureId=${task.originCaptureId}`}
              className="meta-value text-sky-500 hover:text-sky-700 transition-colors"
            >
              Capture
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
